/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/fileHelpers.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    File handling utilities for MOH Sources.
    Handles file validation, preview URLs, etc.
────────────────────────────────────────────────────────────────*/

import { isValidAudioFile, isValidFileSize } from './validators';

/**
 * Generate preview URL for a file
 */
export function createFilePreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke preview URL
 */
export function revokeFilePreviewUrl(url: string): void {
  if (url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch (e) {
      // Ignore errors
    }
  }
}

/**
 * Validate and filter files
 */
export function validateFiles(files: File[]): {
  valid: File[];
  errors: string[];
} {
  const valid: File[] = [];
  const errors: string[] = [];

  for (const file of files) {
    if (!isValidAudioFile(file)) {
      errors.push(`${file.name}: Invalid file type. Allowed: .wav, .mp3, .ogg`);
      continue;
    }

    if (!isValidFileSize(file)) {
      errors.push(`${file.name}: File size must be less than 100MB`);
      continue;
    }

    valid.push(file);
  }

  return { valid, errors };
}

/**
 * Generate class name from name (sanitized)
 */
export function generateClassName(name: string): string {
  if (!name.trim()) return 'default';
  return (
    name
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .toLowerCase() || 'default'
  );
}

